const { taxRates } = require('./config');
const { excludeTax } = require('./util');

// effectiveDate を 'yyyy-MM-dd' に揃える。
// Excel で CSV を開くと 'yyyy-MM-dd' が日付型と認識され 'yyyy/M/d' で保存し直されるため、
// スラッシュ区切りのまま保存された履歴が実在する。文字列比較では '/'(0x2F) > '-'(0x2D) となり
// 同一年の販売日に対して常に false（＝履歴が効かず原価が原始値に落ちる）になるので、比較前に正規化する。
const normalizeEffectiveDate = (value) => {
  if (typeof value !== 'string') return value;
  const matched = value.trim().match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  return matched
    ? `${matched[1]}-${matched[2].padStart(2, '0')}-${matched[3].padStart(2, '0')}`
    : value.trim();
};

const normalizedEntries = (product) => {
  return (product?.unitCostHistory || [])
    .filter(_ => _ && _.effectiveDate != null)
    .map(_ => ({ ..._, effectiveDate: normalizeEffectiveDate(_.effectiveDate) }));
};

// unitCostHistory: [{ effectiveDate: 'yyyy-MM-dd', unitCost: number }]
// 販売日(dateStr, 'yyyy-MM-dd')に対して effectiveDate <= dateStr の最新エントリの unitCost を返す。
// 該当する履歴が無ければ原始原価 product.unitCost にフォールバックする。
const resolveUnitCostAt = (product, dateStr) => {
  const applicable = normalizedEntries(product)
    .filter(_ => _.effectiveDate <= dateStr)
    .sort((a, b) => (a.effectiveDate < b.effectiveDate ? -1 : 1));
  const latest = applicable[applicable.length - 1];
  return latest ? (latest.unitCost || 0) : (product?.unitCost || 0);
};

// 税抜換算込みで日付時点の原価を返す。costTaxRate は履歴化しないため常に現在値を使う。
const resolveUnitCostAtWithTax = (product, dateStr, isExcludeTax) => {
  const cost = resolveUnitCostAt(product, dateStr);
  return isExcludeTax ? excludeTax(cost, taxRates[product?.costTaxRate]?.value) : cost;
};

// 現在時点で有効な最新原価（履歴の最大 effectiveDate、無ければ原始原価）。在庫評価額などに使う。
const resolveLatestUnitCost = (product) => {
  const withDate = normalizedEntries(product);
  if (withDate.length === 0) return product?.unitCost || 0;
  const latest = withDate.reduce((x, _) => (_.effectiveDate > x.effectiveDate ? _ : x));
  return latest.unitCost || 0;
};

const resolveLatestUnitCostWithTax = (product, isExcludeTax) => {
  const cost = resolveLatestUnitCost(product);
  return isExcludeTax ? excludeTax(cost, taxRates[product?.costTaxRate]?.value) : cost;
};

// 原価・配送料を掛ける数量。売上側が netSales で返品・Vine を除いているため数量も揃える。
// salesData は売上doc1件分の values（quantity / refundQuantity / vineQuantity）。
const netQuantityOf = (salesData) => {
  const quantity = salesData?.quantity || 0;
  const refundQuantity = salesData?.refundQuantity || 0;
  const vineQuantity = salesData?.vineQuantity || 0;
  return Math.max(quantity - refundQuantity - vineQuantity, 0);
};

// 配送料を掛ける数量。FBA出荷分はFBA手数料で計上済みなので自己発送分だけに絞る。
// selfDeliveryQuantity を持たない売上（Amazon以外のモール、対応前のAmazon売上）は全数を自己発送とみなす。
const shippingQuantityOf = (salesData) => {
  if (salesData?.selfDeliveryQuantity == null) return netQuantityOf(salesData);
  return netQuantityOf({
    quantity: salesData.selfDeliveryQuantity,
    refundQuantity: salesData.selfDeliveryRefundQuantity,
    vineQuantity: salesData.selfDeliveryVineQuantity,
  });
};

exports.normalizeEffectiveDate = normalizeEffectiveDate;
exports.netQuantityOf = netQuantityOf;
exports.shippingQuantityOf = shippingQuantityOf;
exports.resolveUnitCostAt = resolveUnitCostAt;
exports.resolveUnitCostAtWithTax = resolveUnitCostAtWithTax;
exports.resolveLatestUnitCost = resolveLatestUnitCost;
exports.resolveLatestUnitCostWithTax = resolveLatestUnitCostWithTax;
